'use client';

import { c as _c } from "react/compiler-runtime";
import { type ReactNode, useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { useInstantMotion } from '../../use-instant-motion';

/**
 * Confetti — a self-contained, dependency-free celebratory particle burst for the Labs completion /
 * reveal moments (M2 archetype reveal, M4 finish). NO third-party lib (no canvas-confetti):
 * ~`count` colored squares are positioned via `motion` and animated outward from the center-top,
 * then fall + spin + fade. It is a pure overlay.
 *
 * LAYOUT-NEUTRAL: the root is `absolute inset-0 pointer-events-none overflow-hidden` so it sits over
 * whatever it is mounted inside (a `relative` parent) and NEVER affects layout or intercepts input.
 *
 * DETERMINISM CONTRACT (keeps the visual baselines + reduced-motion users unchanged): under
 * `useInstantMotion()` (reduced-motion OR automation — Playwright `navigator.webdriver` / dev
 * `__labsNoAuto`) it renders NOTHING. Particle trajectories are derived purely from the particle
 * INDEX (angle = i/count · 2π, plus index-hashed jitter) — NEVER `Math.random()`. The burst fires
 * once, runs for `durationMs`, then unmounts its particles (no infinite loop).
 */

export interface ConfettiProps {
  /**
   * Optional accent color woven into the palette — e.g. the active archetype accent on the reveal.
   * When set, it is interleaved so the celebration feels themed to the screen.
   */
  accent?: string;
  /**
   * Particle palette. Defaults to the Extern brand set (brand blue + success green + gold). A pack
   * can theme the whole burst to its axis by passing its two pole accents here — the defaults are
   * design-system colors, not a vertical's colors, so overriding is the vertical's job, not a fork.
   */
  colors?: readonly string[];
  /** Particle count (~30–60 reads well). Default 44. */
  count?: number;
  /** Total burst lifetime in ms before particles are torn down. Default 1400. */
  durationMs?: number;
  /**
   * Fire trigger. Defaults to `true` ⇒ auto-fire once on mount. Pass a changing value to re-fire
   * (each distinct truthy value starts a fresh burst). `false`/`undefined` keeps it dormant.
   */
  fire?: boolean | number | string;
}

/** Extern brand default palette: brand blue (#2853d2) + success green (#006e20) + warm gold. */
const DEFAULT_COLORS = ['#2853d2', '#006e20', '#B8860B'] as const;

/** Deterministic 0..1 pseudo-value from an integer seed — a tiny index hash, NOT Math.random(). */
function hash01(seed: number): number {
  const x = Math.sin(seed * 12.9898) * 43_758.5453;
  return x - Math.floor(x);
}
interface Particle {
  color: string;
  /** Start delay fraction (staggers the burst slightly). */
  delay: number;
  /** Horizontal drift at end of burst, px (signed). */
  dx: number;
  /** Final fall offset, px (positive = down past origin). */
  fall: number;
  /** Peak rise before falling, px (negative = up). */
  rise: number;
  /** Per-particle size in px. */
  size: number;
  /** Total rotation in degrees over the burst. */
  spin: number;
}

/**
 * Build the particle set deterministically from the index. Spread is an even fan
 * (angle = i/count · 2π) so the burst is symmetric; per-index hashing adds organic variance
 * without any RNG.
 */
function buildParticles(count: number, colors: readonly string[]): Particle[] {
  const out: Particle[] = [];
  for (let i = 0; i < count; i += 1) {
    const angle = i / count * Math.PI * 2; // even fan around the circle
    const h1 = hash01(i + 1); // 0..1
    const h2 = hash01(i * 2.17 + 7);
    const h3 = hash01(i * 0.733 + 13);
    const spread = 120 + h1 * 140; // 120..260px horizontal reach
    out.push({
      color: colors[i % colors.length] ?? colors[0] ?? DEFAULT_COLORS[0],
      dx: Math.cos(angle) * spread,
      rise: -(70 + h2 * 120),
      // launch up 70..190px
      fall: 220 + h3 * 200,
      // then fall 220..420px below origin
      spin: (i % 2 === 0 ? 1 : -1) * (180 + h2 * 540),
      // ±180..720°
      size: 7 + Math.round(h3 * 6),
      // 7..13px
      delay: h1 * 0.12 // 0..120ms stagger
    });
  }
  return out;
}
export function Confetti(t0) {
  const $ = _c(14);
  const {
    colors,
    count: t1,
    durationMs: t2,
    accent,
    fire: t3
  } = t0;
  const count = t1 === undefined ? 44 : t1;
  const durationMs = t2 === undefined ? 1400 : t2;
  const fire = t3 === undefined ? true : t3;
  const instant = useInstantMotion();
  const [burstKey, setBurstKey] = useState(0);
  const [active, setActive] = useState(false);
  let t4;
  bb0: {
    const base = colors && colors.length > 0 ? colors : DEFAULT_COLORS;
    if (!accent) {
      t4 = base;
      break bb0;
    }
    let t5;
    if ($[0] !== accent) {
      t5 = c => c.toLowerCase() !== accent.toLowerCase();
      $[0] = accent;
      $[1] = t5;
    } else {
      t5 = $[1];
    }
    t4 = [accent, ...base.filter(t5)];
  }
  const palette = t4;
  let t5;
  let t6;
  if ($[2] !== durationMs || $[3] !== fire || $[4] !== instant) {
    t5 = () => {
      if (instant) {
        return;
      }
      if (!fire) {
        return;
      }
      setBurstKey(_temp);
      setActive(true);
      const t = setTimeout(() => setActive(false), durationMs + 200);
      return () => clearTimeout(t);
    };
    t6 = [fire, instant, durationMs];
    $[2] = durationMs;
    $[3] = fire;
    $[4] = instant;
    $[5] = t5;
    $[6] = t6;
  } else {
    t5 = $[5];
    t6 = $[6];
  }
  useEffect(t5, t6);
  if (instant || !active) {
    return null;
  }
  const particles = buildParticles(count, palette);
  const t7 = true;
  const t8 = "pointer-events-none absolute inset-0 z-10 overflow-hidden";
  const t9 = "absolute top-[38%] left-1/2";
  let t10;
  if ($[7] !== durationMs) {
    t10 = (p, i) => <motion.span animate={{
      x: [0, p.dx * 0.45, p.dx],
      y: [0, p.rise, p.fall],
      opacity: [0, 1, 1, 0],
      rotate: [0, p.spin * 0.5, p.spin],
      scale: [0.6, 1, 1]
    }} className="absolute block rounded-[2px]" initial={{
      x: 0,
      y: 0,
      opacity: 0,
      rotate: 0,
      scale: 0.6
    }} key={i} style={{
      width: p.size,
      height: p.size * 0.62,
      backgroundColor: p.color,
      willChange: "transform, opacity"
    }} transition={{
      duration: durationMs / 1000,
      delay: p.delay,
      ease: [0.22, 1, 0.36, 1],
      times: [0, 0.18, 1],
      opacity: {
        duration: durationMs / 1000,
        delay: p.delay,
        times: [0, 0.12, 0.7, 1]
      }
    }} />;
    $[7] = durationMs;
    $[8] = t10;
  } else {
    t10 = $[8];
  }
  const t11 = particles.map(t10);
  let t12;
  if ($[9] !== t11) {
    t12 = <div className={t9}>{t11}</div>;
    $[9] = t11;
    $[10] = t12;
  } else {
    t12 = $[10];
  }
  let t13;
  if ($[11] !== burstKey || $[12] !== t12) {
    t13 = <div aria-hidden={t7} className={t8} key={burstKey}>{t12}</div>;
    $[11] = burstKey;
    $[12] = t12;
    $[13] = t13;
  } else {
    t13 = $[13];
  }
  return t13;
}
function _temp(k) {
  return k + 1;
}